import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import { defineComponent, reactive, ref, computed, onMounted } from '@vue/composition-api';
import Tip from '@/uikit/Tip.vue';
import BookingsAccordion from '@/Booking/components/BookingsAccordion.vue';
import BookingsSummary from '@/Booking/components/BookingsSummary.vue';
import PageLoadingBar from '@/shared/components/PageLoadingBar.vue';
import InputHeader from '@/components/InputHeader.vue';
import { useAllUserBookingsQuery } from '@/generated-types/graphql.types';
import { mapBookings, getBookingsSummaryData, groupBookingsByDate } from '@/Booking/utils/bookings';
export default defineComponent({
    name: 'CustomerBookings',
    components: {
        DatePicker,
        InputHeader,
        Tip,
        BookingsSummary,
        BookingsAccordion,
        PageLoadingBar
    },
    setup(_, context) {
        const { root } = context;
        const datePickerLocale = {
            formatLocale: {
                firstDayOfWeek: 1
            }
        };
        function formatDate(date) {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        }
        function getCurrentWeekBoundaries() {
            const now = new Date();
            const currentDay = now.getDay();
            const startDate = new Date(now);
            const endDate = new Date(now);
            // get current week Monday
            const diff = currentDay === 0 ? -6 : 1 - currentDay;
            startDate.setDate(now.getDate() + diff);
            // get current week Sunday
            endDate.setDate(startDate.getDate() + 6);
            return {
                start: formatDate(startDate),
                end: formatDate(endDate)
            };
        }
        const weekBoundaries = ref(getCurrentWeekBoundaries());
        const fromDate = ref(weekBoundaries.value.start);
        const toDate = ref(weekBoundaries.value.end);
        const dateError = ref('');
        const queryVariables = reactive({
            filters: {
                date_start: fromDate.value,
                date_end: toDate.value
            }
        });
        const queryOptions = reactive({
            enabled: false,
            fetchPolicy: 'no-cache'
        });
        const { result, loading, refetch } = useAllUserBookingsQuery(queryVariables, queryOptions);
        const isDefaultRange = computed(() => {
            return (fromDate.value === weekBoundaries.value.start &&
                toDate.value === weekBoundaries.value.end);
        });
        const isValidDateFormat = (dateStr) => {
            const regex = /^(\d{2})-(\d{2})-(\d{4})$/;
            const match = dateStr.match(regex);
            if (!match)
                return false;
            const day = parseInt(match[1], 10);
            const month = parseInt(match[2], 10);
            const year = parseInt(match[3], 10);
            if (month < 1 || month > 12)
                return false;
            if (day < 1 || day > 31)
                return false;
            if (year < 2000 || year > 3000)
                return false;
            const date = new Date(year, month - 1, day);
            return (date.getDate() === day &&
                date.getMonth() === month - 1 &&
                date.getFullYear() === year);
        };
        const parseDate = (dateStr) => {
            if (!isValidDateFormat(dateStr))
                return null;
            const [day, month, year] = dateStr.split('-').map(Number);
            return new Date(year, month - 1, day);
        };
        const validateDateRange = () => {
            const fromDateObj = parseDate(fromDate.value);
            const toDateObj = parseDate(toDate.value);
            if (!fromDateObj || !toDateObj) {
                dateError.value = root.$i18n.t('booking.user_bookings.invalid_date_format');
                return false;
            }
            if (fromDateObj > toDateObj) {
                dateError.value = root.$i18n.t('booking.user_bookings.invalid_date_range');
                return false;
            }
            dateError.value = '';
            return true;
        };
        const isDateRangeValid = computed(() => {
            return validateDateRange() && dateError.value === '';
        });
        const applyDateFilter = () => {
            if (!isDateRangeValid.value)
                return;
            queryVariables.filters = {
                date_start: fromDate.value,
                date_end: toDate.value
            };
            refetch(queryVariables);
            weekBoundaries.value.start = fromDate.value;
            weekBoundaries.value.end = toDate.value;
        };
        const navigateWeek = (direction) => {
            const fromDateObj = parseDate(fromDate.value);
            const toDateObj = parseDate(toDate.value);
            if (!fromDateObj || !toDateObj)
                return;
            const diff = direction === 1 ? 7 : -7;
            fromDateObj.setDate(fromDateObj.getDate() + diff);
            toDateObj.setDate(toDateObj.getDate() + diff);
            fromDate.value = formatDate(fromDateObj);
            toDate.value = formatDate(toDateObj);
        };
        const bookings = computed(() => result.value?.get_bookings || []);
        const bookingsByType = computed(() => mapBookings(bookings.value));
        const accordionBookingsData = computed(() => groupBookingsByDate(bookingsByType.value));
        const bookingsSummaryData = computed(() => getBookingsSummaryData(bookingsByType.value));
        const hasBookings = computed(() => !!bookings.value.length);
        function resetDateFilter() {
            const currentWeek = getCurrentWeekBoundaries();
            fromDate.value = currentWeek.start;
            toDate.value = currentWeek.end;
            dateError.value = '';
        }
        function disableUnavailableFromDates(date) {
            const minDate = new Date(2019, 0, 1);
            return date < minDate;
        }
        function disableUnavailableToDates(date) {
            const maxDate = new Date(2100, 11, 31);
            const fromDateObj = parseDate(fromDate.value);
            return date < fromDateObj || date > maxDate;
        }
        onMounted(() => {
            queryOptions.enabled = true;
            refetch(queryVariables);
        });
        return {
            accordionBookingsData,
            bookingsSummaryData,
            dateError,
            datePickerLocale,
            fromDate,
            hasBookings,
            isDateRangeValid,
            isDefaultRange,
            loading,
            toDate,
            applyDateFilter,
            disableUnavailableFromDates,
            disableUnavailableToDates,
            navigateWeek,
            resetDateFilter,
            validateDateRange
        };
    }
});
